import { formatDateToZ } from "util/datelib";

/** Rows from `data/mixed` (hour bucket) for `lora.gateway` aggregates. */
export type GatewayAggregateRow = {
  timestamp?: string;
  _id?: string | { $date?: string };
  hasData?: boolean;
};

export type GatewayChartFilter = {
  calendar: "hour" | "month" | "year";
  xFormat: "day" | "month" | "year";
};

function rowTimestamp(row: GatewayAggregateRow): string | null {
  if (row.timestamp != null && row.timestamp !== "") {
    return String(row.timestamp);
  }
  const id = row._id;
  if (id == null) return null;
  if (typeof id === "object" && "$date" in id && id.$date != null) {
    return String(id.$date);
  }
  if (typeof id === "string") return id;
  return null;
}

/** Match `formatDateTimezone`: device-local wall time from a UTC instant. */
export function gatewayDeviceWallDate(
  utcInstant: Date,
  timezoneHours?: number
): Date {
  const tz = timezoneHours ?? 0;
  const utc = utcInstant.getTime() + utcInstant.getTimezoneOffset() * 60000;
  return new Date(utc + tz * 3600000);
}

function hourKeyWall(wall: Date): string {
  return `${wall.getFullYear()}-${wall.getMonth()}-${wall.getDate()}-${wall.getHours()}`;
}

export function indexGatewayHourlyHasData(
  rows: GatewayAggregateRow[],
  timezoneHours?: number
): Map<string, boolean> {
  const map = new Map<string, boolean>();
  for (const row of rows) {
    const ts = rowTimestamp(row);
    if (!ts) continue;
    const key = hourKeyWall(gatewayDeviceWallDate(new Date(ts), timezoneHours));
    const v = Boolean(row.hasData);
    map.set(key, v || map.get(key) === true);
  }
  return map;
}

function hoursInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate() * 24;
}

export type GatewayChartSeries = {
  labels: string[];
  values: number[];
  /** 0–1 hourly bars vs 0–100 percentage bars */
  valueScale: "binary" | "percent";
};

/**
 * Builds chart series from hourly gateway aggregate rows.
 * - Hours: one selected day, value 1 if hasData else 0 per hour.
 * - Days in month: each day, % of 24 hours with hasData.
 * - Months in year: each month, % of hours in that month with hasData.
 */
export function buildGatewayChartSeries(
  filter: GatewayChartFilter,
  anchorDate: Date,
  timezoneHours: number | undefined,
  rows: GatewayAggregateRow[]
): GatewayChartSeries {
  const map = indexGatewayHourlyHasData(rows, timezoneHours);
  const rangeStartIso = formatDateToZ(
    anchorDate,
    false,
    filter.xFormat,
    timezoneHours
  );
  const wall = gatewayDeviceWallDate(new Date(rangeStartIso), timezoneHours);
  const y = wall.getFullYear();
  const m = wall.getMonth();

  if (filter.calendar === "hour") {
    const d = wall.getDate();
    const labels: string[] = [];
    const values: number[] = [];
    for (let h = 0; h < 24; h++) {
      const key = `${y}-${m}-${d}-${h}`;
      labels.push(String(h));
      values.push(map.get(key) === true ? 1 : 0);
    }
    return { labels, values, valueScale: "binary" };
  }

  if (filter.calendar === "month") {
    const dim = new Date(y, m + 1, 0).getDate();
    const labels: string[] = [];
    const values: number[] = [];
    for (let day = 1; day <= dim; day++) {
      let withData = 0;
      for (let h = 0; h < 24; h++) {
        const key = `${y}-${m}-${day}-${h}`;
        if (map.get(key) === true) withData++;
      }
      labels.push(String(day));
      values.push((withData / 24) * 100);
    }
    return { labels, values, valueScale: "percent" };
  }

  // Selected calendar year: one bar per month (% of hours with hasData)
  const labels: string[] = [];
  const values: number[] = [];
  for (let month = 0; month < 12; month++) {
    const totalH = hoursInMonth(y, month);
    let withData = 0;
    const dim = new Date(y, month + 1, 0).getDate();
    for (let day = 1; day <= dim; day++) {
      for (let h = 0; h < 24; h++) {
        const key = `${y}-${month}-${day}-${h}`;
        if (map.get(key) === true) withData++;
      }
    }
    labels.push(
      new Date(y, month, 1).toLocaleString(undefined, { month: "short" })
    );
    values.push(totalH > 0 ? (withData / totalH) * 100 : 0);
  }
  return { labels, values, valueScale: "percent" };
}
